import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import MoveItemsModal from '../../../../components/move-items-modal'
import { useSnackbar } from '../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../components/stories/molecules/snackbar/constants'
import { useMoveProjectsAndFoldersMutation } from '../../../../react-query/project/mutation/use-move-projects-and-folders-mutation'
import {
  projectMenuApiErrorCodeOptions,
  projectMenuItemTypeOptions
} from '../../constants'
import { useCheckProjectChildrenNameIsAvailableMutation } from '../../../../react-query/project/mutation/use-check-project-children-name-is-available-mutation'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import NotAllowedMoveItemsAlert from '../../../../components/move-items-modal/not-allowed-move-items-alert'

const CompanyMoveMultipleItemsModal = ({
  onClose = () => {},
  onMoved = () => {},
  selectedFolders = [],
  selectedProjects = []
}) => {
  const { formatMessage } = useIntl()
  const { addSnackbar } = useSnackbar()
  const [moveItemsError, setMoveItemsError] = useState('')

  // items currently targeted by the move action - starts as the items
  // received via props, but gets replaced with only the blocked items
  // when the user chooses to move the remaining items after a partial move
  const [selectedItems, setSelectedItems] = useState({
    folders: selectedFolders,
    projects: selectedProjects
  })

  // items blocked by the last move attempt (name conflict in destination)
  const [blockedItems, setBlockedItems] = useState({
    folders: [],
    projects: []
  })

  const selected = useMemo(
    () => ({
      folders: {
        ids: selectedItems.folders.map((f) => f.id),
        names: selectedItems.folders.map((f) => f.name)
      },
      projects: {
        ids: selectedItems.projects.map((p) => p.id),
        names: selectedItems.projects.map((p) => p.name)
      }
    }),
    [selectedItems]
  )

  const totalSelectedItems =
    selected.folders.ids.length + selected.projects.ids.length

  const hasBlockedItems =
    !isEmpty(blockedItems.folders) || !isEmpty(blockedItems.projects)

  const blockedItemsList = useMemo(
    () => [
      ...blockedItems.folders.map((folder) => ({
        name: folder.name,
        itemType: projectMenuItemTypeOptions.FOLDER
      })),
      ...blockedItems.projects.map((project) => ({
        name: project.name,
        itemType: projectMenuItemTypeOptions.PROJECT
      }))
    ],
    [blockedItems]
  )

  const [destinationFolderHint, setDestinationFolderHint] = useState(null)

  // handlers
  const showSnackbarSuccess = () => {
    addSnackbar({
      message: formatMessage({ id: 'moveItems_items_moved' }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  // replaces the selected items in state with only the items that were
  // blocked on the last move attempt, and closes the blocked items alert
  const handleMoveRemainingItems = () => {
    setSelectedItems({
      folders: blockedItems.folders,
      projects: blockedItems.projects
    })
    setBlockedItems({ folders: [], projects: [] })
  }

  // handle callbacks
  const handleMoveItemsSuccess = () => {
    // there are still blocked items pending review, keep the modal open
    // so the user can decide what to do with them
    if (hasBlockedItems) return null

    if (isEmpty(moveItemsError)) {
      onClose()
      onMoved()
      showSnackbarSuccess()
    }
  }

  const handleMoveItemsError = (error) => {
    // user doesn't have permission to move items
    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.NO_PERMISSION_TO_CREATE_PROJECT_OR_FOLDER
    ) {
      return setMoveItemsError(
        formatMessage({
          id: 'projectMenu_no_permission_to_move'
        })
      )
    }

    // folder with the same name already exists in the destination folder
    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.FOLDER_NAME_IS_NOT_AVAILABLE
    ) {
      return setDestinationFolderHint(
        formatMessage({
          id: 'projectMenu_folder_with_this_name_aready_exists'
        })
      )
    }

    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.FOLDER_NO_LOGER_EXISTS
    ) {
      return setDestinationFolderHint(
        formatMessage({
          id: 'recursiveFolderList_the_selected_folder_no_longer_exists'
        })
      )
    }

    return setMoveItemsError(
      formatMessage({
        id: 'moveItems_move_error'
      })
    )
  }

  const handleCheckMoveItemsNameSuccess = (data, variables) => {
    const { projectNames: checkedItemNames } = data
    const { destinationFolderId } = variables

    // items already inside the destination folder must not be moved
    const namesAlreadyInDestination = [
      ...selectedItems.folders,
      ...selectedItems.projects
    ]
      .filter((item) => item.parentId === destinationFolderId)
      .map((item) => item.name)

    // split the object into 2 arrays - allowed and blocked
    const allowedNames = Object.entries(checkedItemNames)
      .filter(
        ([name, item]) =>
          item.allowed && !namesAlreadyInDestination.includes(name)
      )
      .map(([name]) => name)

    // move only allowed items
    const allowedFolderIds = selectedItems.folders
      .filter((folder) => allowedNames.includes(folder.name))
      .map((folder) => folder.id)

    const allowedProjectIds = selectedItems.projects
      .filter((project) => allowedNames.includes(project.name))
      .map((project) => project.id)

    // keep track of the blocked items so they can replace the current
    // selection if the user chooses to move the remaining items
    const newBlockedFolders = selectedItems.folders.filter(
      (folder) => !allowedNames.includes(folder.name)
    )
    const newBlockedProjects = selectedItems.projects.filter(
      (project) => !allowedNames.includes(project.name)
    )

    if (!isEmpty(newBlockedFolders) || !isEmpty(newBlockedProjects)) {
      setBlockedItems({
        folders: newBlockedFolders,
        projects: newBlockedProjects
      })
    }

    if (isEmpty(allowedProjectIds) && isEmpty(allowedFolderIds)) return null

    moveItems({
      projectIds: allowedProjectIds,
      folderIds: allowedFolderIds,
      destinationFolderId
    })
  }

  const handleCheckMoveItemsNameError = () => {
    return setMoveItemsError(
      formatMessage({
        id: 'moveItems_move_error'
      })
    )
  }

  const handleCloseMoveRemainingItems = () => {
    onClose()
    onMoved()
    showSnackbarSuccess()
  }

  // mutations

  const { mutate: moveItems, isPending: moveItemsLoading } =
    useMoveProjectsAndFoldersMutation({
      onSuccess: handleMoveItemsSuccess,
      onError: handleMoveItemsError
    })

  const { mutate: checkMoveItems, isPending: checkMoveItemsLoading } =
    useCheckProjectChildrenNameIsAvailableMutation({
      onSuccess: handleCheckMoveItemsNameSuccess,
      onError: handleCheckMoveItemsNameError
    })

  const handleMoveItems = ({ parentFolderId = null }) => {
    setMoveItemsError(null)
    setDestinationFolderHint(null)

    const selectedItemNames = [
      ...selected.folders.names,
      ...selected.projects.names
    ]

    checkMoveItems({
      destinationFolderId: parentFolderId,
      selectedItemNames
    })
  }

  const isLoading = moveItemsLoading || checkMoveItemsLoading

  if (totalSelectedItems === 0) return null

  return (
    <>
      <NotAllowedMoveItemsAlert
        isOpen={hasBlockedItems}
        blockedItems={blockedItemsList}
        totalItems={totalSelectedItems}
        onClose={handleCloseMoveRemainingItems}
        onMoveRemaining={handleMoveRemainingItems}
      />

      {!hasBlockedItems && (
        <MoveItemsModal
          handleClose={onClose}
          handleMove={handleMoveItems}
          isLoading={false}
          onMoveLoading={isLoading}
          onMoveError={moveItemsError}
          destinationFolderHint={destinationFolderHint}
          modalTitle={formatMessage({ id: 'move' })}
          isMultipleItems={true}
          totalSelectedItems={totalSelectedItems}
        />
      )}
    </>
  )
}

CompanyMoveMultipleItemsModal.propTypes = {
  onClose: PropTypes.func,
  onMoved: PropTypes.func,
  selectedFolders: PropTypes.array,
  selectedProjects: PropTypes.array
}

export default CompanyMoveMultipleItemsModal
